/**
 * 开发环境工具函数
 * 提供统一的开发环境判断和日志输出方法
 */

import wetvKernel from '@tencent/wetv-kernel';
import { getCookieAmend } from './apiUtils';
import { VERSION } from '@/config/version';

/**
 * 判断是否为开发环境
 * @returns 是否为开发环境
 */
export const isDevelopment = (): boolean => {
  return process.env.NODE_ENV === 'development';
};

/**
 * 节流配置
 */
const THROTTLE_CONFIG = {
  // 默认节流间隔（毫秒）
  DEFAULT_INTERVAL: 200,
} as const;

/**
 * 日志节流状态缓存
 * key: 日志标识（基于第一个参数生成），value: 节流状态
 */
const logThrottleCache = new Map<
  string,
  { lastTime: number; timeoutId: NodeJS.Timeout | null; lastArgs: unknown[] | null }
>();

/**
 * 生成日志标识
 * 基于第一个参数生成唯一标识，用于区分不同的日志
 * @param firstArg - 第一个参数
 * @returns 日志标识
 */
function getLogKey(firstArg: unknown): string {
  if (typeof firstArg === 'string') {
    return firstArg;
  }
  if (typeof firstArg === 'object' && firstArg !== null) {
    try {
      return JSON.stringify(firstArg);
    } catch {
      return String(firstArg);
    }
  }
  return String(firstArg);
}

/**
 * 带节流的日志输出实现
 * @param consoleMethod - console 方法（log, warn, error 等）
 * @param args - 日志参数
 * @param throttle - 是否启用节流
 */
function throttledLog(consoleMethod: (...args: unknown[]) => void, args: unknown[], throttle = true): void {
  if (!isDevelopment()) {
    return;
  }

  if (args.length === 0) {
    consoleMethod();
    return;
  }

  // 如果不节流，直接输出
  if (!throttle) {
    consoleMethod(...args);
    return;
  }

  // 节流逻辑
  const logKey = getLogKey(args[0]);
  const now = Date.now();
  const cache = logThrottleCache.get(logKey);

  // 如果没有缓存或距离上次执行时间超过间隔，立即执行
  if (!cache || now - cache.lastTime >= THROTTLE_CONFIG.DEFAULT_INTERVAL) {
    // 清除之前的定时器
    if (cache?.timeoutId) {
      clearTimeout(cache.timeoutId);
    }

    consoleMethod(...args);
    logThrottleCache.set(logKey, {
      lastTime: now,
      timeoutId: null,
      lastArgs: null,
    });
  } else {
    // 否则，保存参数并设置定时器在间隔时间后执行最后一次调用
    cache.lastArgs = args;
    if (!cache.timeoutId) {
      cache.timeoutId = setTimeout(
        () => {
          const cached = logThrottleCache.get(logKey);
          if (cached?.lastArgs) {
            consoleMethod(...cached.lastArgs);
            cached.lastTime = Date.now();
            cached.lastArgs = null;
          }
          if (cached) {
            cached.timeoutId = null;
          }
        },
        THROTTLE_CONFIG.DEFAULT_INTERVAL - (now - cache.lastTime)
      );
    }
  }
}

/**
 * 日志工具对象
 * 提供统一的日志接口，开发环境输出日志，生产环境静默（error 除外）
 *
 * @example
 * ```ts
 * import { logger } from '@/utils/devUtils';
 *
 * logger.log('普通日志'); // 带节流，开发环境输出
 * logger.debug('调试日志'); // 立即输出，开发环境输出
 * logger.info('信息日志'); // 立即输出，开发环境输出
 * logger.warn('警告日志'); // 立即输出，开发环境输出
 * logger.error('错误日志'); // 立即输出，所有环境都输出
 * ```
 */
export const logger = {
  /**
   * 普通日志输出（带节流）
   * 仅在开发环境下输出，自动节流以避免高频调用
   * 默认节流间隔为 200ms，确保在间隔内最多输出一次
   * 不同日志（基于第一个参数区分）维护独立的节流状态，互不影响
   * @param args - 日志参数
   */
  log: (...args: unknown[]): void => {
    throttledLog(console.log, args, true);
  },

  /**
   * 调试日志输出（立即执行，不节流）
   * 仅在开发环境下输出，用于调试信息，需要立即看到
   * @param args - 调试参数
   */
  debug: (...args: unknown[]): void => {
    throttledLog(console.debug, args, false);
  },

  /**
   * 信息日志输出（立即执行，不节流）
   * 仅在开发环境下输出
   * @param args - 信息参数
   */
  info: (...args: unknown[]): void => {
    throttledLog(console.info, args, false);
  },

  /**
   * 警告日志输出（立即执行，不节流）
   * 仅在开发环境下输出
   * @param args - 警告参数
   */
  warn: (...args: unknown[]): void => {
    throttledLog(console.warn, args, false);
  },

  /**
   * 错误日志输出（立即执行，不节流）
   * 错误日志在所有环境都输出，便于排查问题
   * @param args - 错误参数
   */
  error: (...args: unknown[]): void => {
    // 错误日志在所有环境都输出
    console.error(...args);

    // 上报错误到 ageisReportor 并存储到离线日志
    try {
      reportEvent({
        name: 'error',
        message: args.map((arg) => (typeof arg === 'string' ? arg : JSON.stringify(arg))).join(' '),
        args,
      });
    } catch (error) {
      // 上报失败不影响原有错误输出
      console.warn('[devUtils] 错误上报失败:', error);
    }
  },
};

/**
 * 错误上报接口
 * 参考 tv-linux/src/util/dataReporting.js 的 errorLogReport 方法
 */
export interface ReportEventData {
  /** 事件名称 */
  name?: string;
  /** 错误消息 */
  message?: string;
  /** 错误代码 */
  code?: string;
  /** 错误路径/URL */
  path?: string;
  /** 错误参数 */
  params?: Record<string, any>;
  /** 错误数据 */
  data?: any;
  /** 错误代码（兼容旧格式） */
  err_code?: string;
  /** 错误消息（兼容旧格式） */
  err_msg?: string;
  /** 错误内容 */
  content?: string;
  /** 原始参数（用于日志） */
  args?: unknown[];
  /** 扩展字段1 */
  ext1?: string;
  /** 扩展字段3 */
  ext3?: string;
}

/**
 * 上报事件到 ageisReportor 并存储到离线日志
 * 参考 tv-linux/src/util/dataReporting.js 的 errorLogReport 方法
 *
 * @param data 上报数据
 *
 * @example
 * ```ts
 * import { reportEvent } from '@/utils/devUtils';
 *
 * reportEvent({
 *   name: 'apiError',
 *   path: '/api/video/info',
 *   message: 'Request failed',
 *   code: 'NETWORK_ERROR',
 * });
 * ```
 */
export function reportEvent(data: ReportEventData): void {
  try {
    const { ageisReportor, offlineLog } = wetvKernel;

    // 获取用户信息
    const guid = getCookieAmend('guid') || '';
    const vuid = getCookieAmend('vuserid') || '';
    const uin = vuid ? `${guid}-${vuid}` : `${guid}`;

    // 构建上报参数
    const params = data.params ? JSON.stringify(data.params) : '';
    const reportData = data.data ? JSON.stringify(data.data) : '';
    const ext1Params = data.ext1 || (data.path ? `reqUrl=${data.path}` : location.href);
    const ext3Params = `params=${params}&data=${reportData}&version=${VERSION}&uin=${uin}`;

    // 使用 ageisReportor.reportEvent 上报
    if (ageisReportor?.reportEvent) {
      ageisReportor.reportEvent({
        name: data.name || 'error',
        ext1: ext1Params,
        ext3:
          data.ext3 ||
          `${ext3Params}&errCode=${data.err_code || data.code || ''}&errMsg=${data.err_msg || data.message || ''}&content=${data.content || ''}`,
      });
    }

    // 使用 AegisV2.reportEvent 上报
    if (window.AegisV2?.reportEvent) {
      window.AegisV2.reportEvent({
        name: data.name || 'error',
        ext1: ext1Params,
        ext3:
          data.ext3 ||
          `${ext3Params}&errCode=${data.err_code || data.code || ''}&errMsg=${data.err_msg || data.message || ''}&content=${data.content || ''}`,
      });
    }

    // 存储到离线日志
    if (offlineLog?.logger) {
      const logger = offlineLog.logger('error');
      const errorLogger = logger?.error;
      if (errorLogger) {
        try {
          const logData = {
            name: data.name || 'error',
            message: data.message,
            code: data.code || data.err_code,
            path: data.path,
            params: data.params,
            data: data.data,
            err_code: data.err_code,
            err_msg: data.err_msg,
            content: data.content,
            args: data.args,
            timestamp: new Date().toISOString(),
            uin,
          };
          errorLogger(JSON.stringify(logData));
        } catch (error) {
          // 如果序列化失败，记录简单消息和错误信息
          const errorMessage = error instanceof Error ? error.message : String(error);
          errorLogger(
            `[offlineLog.logger] Log serialization failed: ${data.message || 'Unknown error'}, error: ${errorMessage}`
          );
        }
      }
    }
  } catch (error) {
    // 上报失败不影响应用运行，只输出警告
    console.warn('[reportEvent] 错误上报失败:', error);
  }
}
